/*
 * dvl-housing-guard.js
 * --------------------------------------------------------------------------
 * Prevents the "Please select your housing" placeholder variant from ever
 * being purchased.
 *
 * The placeholder is intentionally an AVAILABLE variant so Shopify selects it
 * as the default (forcing the customer to actively pick a housing). This guard
 * blocks every path to cart/checkout WHILE the placeholder is the selected
 * variant, and shows a message above the buy buttons.
 *
 * Theme-agnostic: identifies the placeholder from /products/<handle>.js, uses
 * generic selectors, and scopes blocking to the specific product form whose
 * selected variant is the placeholder — so other products' add-to-cart buttons
 * on the same page (recommendations, quick-view) are never affected.
 * Inert on products that have no placeholder variant.
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var MESSAGE = 'Please select your housing before adding to cart.';

  // Controls that can move a variant toward cart/checkout: the themed
  // Add-to-Cart submit and the SPO ("Smart Product Options") "Next" button.
  var ACTION_SELECTOR =
    '#addToCart, [name="add"], form[action*="/cart/add"] [type="submit"], .spo-next-button';
  var FORM_SELECTOR = 'form[action*="/cart/add"]';

  var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (!match) return; // product pages only
  var handle = match[1];

  var placeholderIds = new Set();
  var ready = false;

  function isPlaceholder(v) {
    var hay = [v.title, v.option1, v.option2, v.option3].join(' ').toLowerCase();
    return hay.indexOf('please select') !== -1;
  }

  function mainForm() {
    return document.querySelector(FORM_SELECTOR);
  }

  // Does this product form currently have the placeholder variant selected?
  function formIsPlaceholder(form) {
    if (!ready || !form) return false;
    var idEl = form.querySelector('[name="id"]');
    return !!idEl && placeholderIds.has(String(idEl.value));
  }

  // A control's context = its enclosing cart/add form, else the main product
  // form (covers page-level controls such as the SPO drawer button).
  function ctxIsPlaceholder(el) {
    var form = el.closest ? el.closest(FORM_SELECTOR) : null;
    return formIsPlaceholder(form || mainForm());
  }

  // ---- message ----------------------------------------------------------
  function msgBox(create) {
    var box = document.querySelector('.dvl-housing-msg');
    if (!box && create) {
      box = document.createElement('div');
      box.className = 'dvl-housing-msg';
      box.setAttribute('role', 'alert');
      box.style.cssText =
        'color:#b00020;margin:0 0 10px;font-size:14px;font-weight:600;';
      // Above the buy area: anchor to the topmost action/option block present.
      var anchor = document.querySelector(
        '#spo-product-options, .spo-next-button, #addToCart, [name="add"], ' +
          '.product-form__submit, ' +
          FORM_SELECTOR +
          ' [type="submit"], .shopify-payment-button'
      );
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(box, anchor);
    }
    return box;
  }
  // Idempotent writes (no-op when unchanged) so the MutationObserver never
  // reacts to our own DOM edits and loops.
  function showMessage() {
    var b = msgBox(true);
    if (b && b.textContent !== MESSAGE) b.textContent = MESSAGE;
  }
  function clearMessage() {
    var b = msgBox(false);
    if (b && b.textContent !== '') b.textContent = '';
  }

  // ---- control state (idempotent; only touches what we changed) ----------
  function block(el, hide) {
    if (el.getAttribute('data-dvl-blocked')) return;
    if (hide) {
      el.style.display = 'none';
    } else {
      el.disabled = true;
      el.classList.add('disabled');
      el.setAttribute('aria-disabled', 'true');
      el.style.pointerEvents = 'none';
      el.style.opacity = '0.5';
    }
    el.setAttribute('data-dvl-blocked', hide ? 'hide' : '1');
  }
  function unblock(el) {
    var mode = el.getAttribute('data-dvl-blocked');
    if (!mode) return; // we didn't block it
    if (mode === 'hide') {
      el.style.display = '';
    } else {
      el.disabled = false;
      el.classList.remove('disabled');
      el.removeAttribute('aria-disabled');
      el.style.pointerEvents = '';
      el.style.opacity = '';
    }
    el.removeAttribute('data-dvl-blocked');
  }
  function updateButtons() {
    document.querySelectorAll(ACTION_SELECTOR).forEach(function (el) {
      if (ctxIsPlaceholder(el)) block(el, false);
      else unblock(el);
    });
    // Dynamic checkout / Shop Pay buttons bypass the themed add-to-cart.
    document.querySelectorAll('.shopify-payment-button').forEach(function (el) {
      if (ctxIsPlaceholder(el)) block(el, true);
      else unblock(el);
    });
    if (formIsPlaceholder(mainForm())) showMessage();
    else clearMessage();
  }

  // ---- which variant id is a /cart/add request adding? ------------------
  function bodyVariantId(body) {
    if (!body) return null;
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var fv = body.get('id');
        return fv != null ? String(fv) : null;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        var uv = body.get('id');
        return uv != null ? String(uv) : null;
      }
      if (typeof body === 'string') {
        var s = body.replace(/^\s+/, '');
        if (s.charAt(0) === '{') {
          try {
            var j = JSON.parse(s);
            if (j && j.id != null) return String(j.id);
          } catch (e) {}
        }
        var m = body.match(/(?:^|&)id=([^&]+)/);
        if (m) return decodeURIComponent(m[1]);
      }
    } catch (e) {}
    return null;
  }
  function addIsBlocked(body) {
    if (!ready) return false;
    var id = bodyVariantId(body);
    if (id != null) return placeholderIds.has(id);
    return formIsPlaceholder(mainForm()); // id unknown → fall back to selection
  }

  // ---- load placeholder ids for this product ----------------------------
  fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
    .then(function (r) {
      return r.ok ? r.json() : null;
    })
    .then(function (p) {
      if (!p || !p.variants) return;
      p.variants.forEach(function (v) {
        if (isPlaceholder(v)) placeholderIds.add(String(v.id));
      });
      if (placeholderIds.size === 0) return; // nothing to guard on this product
      ready = true;
      bind();
      updateButtons();
    })
    .catch(function () {});

  function bind() {
    ['change', 'click'].forEach(function (evt) {
      document.addEventListener(
        evt,
        function () {
          setTimeout(updateButtons, 0);
        },
        true
      );
    });
    document.addEventListener('variant:changed', function () {
      setTimeout(updateButtons, 0);
    });

    // Synchronously block clicks on a blocked-context action control. Covers
    // <a>/<div> SPO buttons and the moment before async-rendered controls are
    // visually disabled.
    document.addEventListener(
      'click',
      function (e) {
        var t = e.target.closest && e.target.closest(ACTION_SELECTOR);
        if (t && ctxIsPlaceholder(t)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showMessage();
        }
      },
      true
    );

    // Block placeholder form submits.
    document.addEventListener(
      'submit',
      function (e) {
        var f = e.target;
        if (f && f.matches && f.matches(FORM_SELECTOR) && formIsPlaceholder(f)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showMessage();
        }
      },
      true
    );

    // SPO renders/re-renders its options asynchronously; re-apply on DOM change.
    // Writes are idempotent, so a stable state produces no mutations → no loop.
    if (window.MutationObserver) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () {
          pending = false;
          updateButtons();
        }, 50);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  // ---- AJAX backstops: catch every themed ajax-cart path ----------------
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/cart\/add(\.js)?($|\?)/.test(url) && addIsBlocked(init && init.body)) {
        showMessage();
        return Promise.reject(new Error('dvl-housing-guard: housing not selected'));
      }
      return origFetch.apply(this, arguments);
    };
  }
  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__dvlUrl = url || '';
    return xhrOpen.apply(this, arguments);
  };
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (body) {
    if (/\/cart\/add/.test(this.__dvlUrl || '') && addIsBlocked(body)) {
      showMessage();
      return; // abort
    }
    return xhrSend.apply(this, arguments);
  };
})();
